import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowUpRight, FiGithub, FiExternalLink } from 'react-icons/fi';

const ProjectCard = ({ project, index = 0 }) => {
  const techColors = {
    'React': 'bg-cyan-500/10 text-cyan-400',
    'Node.js': 'bg-green-500/10 text-green-400',
    'MongoDB': 'bg-green-600/10 text-green-500',
    'Figma': 'bg-purple-500/10 text-purple-400',
    'Next.js': 'bg-white/10 text-white/70',
    'default': 'bg-purple-500/10 text-purple-300',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="group glass-card overflow-hidden hover:border-purple-500/40 transition-all duration-500"
      style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}
    >
      {/* Image */}
      <div className="relative overflow-hidden h-52">
        <img
          src={project.thumbnail}
          alt={project.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

        {/* Category Badge */}
        <div className="absolute top-4 left-4">
          <span className="px-3 py-1 rounded-full text-xs font-body font-medium"
            style={{ background: 'rgba(127,4,155,0.7)', backdropFilter: 'blur(10px)', color: '#fff' }}>
            {project.category}
          </span>
        </div>

        {/* Featured Badge */}
        {project.featured && (
          <div className="absolute top-4 right-4">
            <span className="px-3 py-1 rounded-full text-xs font-body font-medium bg-yellow-500/80 text-black">
              Featured
            </span>
          </div>
        )}

        {/* Hover overlay */}
        <div className="absolute inset-0 flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          {project.liveUrl && (
            <a href={project.liveUrl} target="_blank" rel="noopener noreferrer"
              className="w-10 h-10 rounded-full flex items-center justify-center bg-white text-black hover:bg-purple-400 transition-colors duration-200">
              <FiExternalLink size={16} />
            </a>
          )}
          {project.githubUrl && (
            <a href={project.githubUrl} target="_blank" rel="noopener noreferrer"
              className="w-10 h-10 rounded-full flex items-center justify-center bg-white/10 text-white hover:bg-white/20 border border-white/20 transition-colors duration-200">
              <FiGithub size={16} />
            </a>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <h3 className="font-display text-xl font-medium text-white mb-2 group-hover:text-purple-300 transition-colors duration-200">
          {project.title}
        </h3>
        <p className="font-body text-sm text-white/50 leading-relaxed mb-4 line-clamp-2">
          {project.description}
        </p>

        {/* Tech Stack */}
        <div className="flex flex-wrap gap-2 mb-5">
          {project.technologies.slice(0, 4).map(tech => (
            <span key={tech}
              className={`px-2 py-0.5 rounded text-xs font-mono ${techColors[tech] || techColors.default}`}>
              {tech}
            </span>
          ))}
          {project.technologies.length > 4 && (
            <span className="px-2 py-0.5 rounded text-xs font-mono text-white/30">
              +{project.technologies.length - 4}
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3">
          <Link
            to={`/projects/${project.id}`}
            className="flex items-center gap-1.5 text-sm font-body font-medium text-purple-400 hover:text-purple-300 transition-colors duration-200"
          >
            View Details <FiArrowUpRight size={14} />
          </Link>
          {project.liveUrl && (
            <a href={project.liveUrl} target="_blank" rel="noopener noreferrer"
              className="ml-auto btn-outline text-xs py-1.5 px-4">
              Live Site
            </a>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default ProjectCard;
